import React from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import clsx from 'clsx';
import styles from './StanTable.module.css';
import IconChevronLeft from '@/assets/icons/ic-chevron-left.svg';
import IconChevronRight from '@/assets/icons/ic-chevron-right.svg';
import IconEmptyTable from '@/assets/icons/ic-empty-table.svg';
import { StanLoader } from '../StanLoader/StanLoader';
import { StanColumnDef, StanTableProps } from './StanTable.types';
import { createIconElement } from '@/lib';

export const StanTable = <T,>({
    title,
    columns = [],
    data = [],
    className = '',
    onRowClick,
    isLoading = false,
    page = 1,
    totalPages,
    onPageChange,
    totalRowsLabel,
    emptyHeader = 'No Entries',
    emptySubheader = '',
    hideHeaderWhenEmpty = false,
}: StanTableProps<T>) => {
    const BareEmptyIcon = createIconElement(IconEmptyTable, { className: styles.emptyIcon, removeFill: false });
    const BarePaginationLeftIcon = createIconElement(IconChevronLeft, { className: styles.paginationIcon });
    const BarePaginationRightIcon = createIconElement(IconChevronRight, { className: styles.paginationIcon });
    // Detect mobile (simple window width check)
    const [isMobile, setIsMobile] = React.useState(false);

    React.useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Filter columns for mobile
    const visibleColumns = columns.filter(col => !(isMobile && col.hideOnMobile));

    const table = useReactTable({
        data,
        columns: visibleColumns,
        getCoreRowModel: getCoreRowModel(),
    });

    // Helper to get alignment class
    const getAlignClass = (align?: 'left' | 'center' | 'right') => {
        if (align === 'right') return styles.alignRight;
        if (align === 'center') return styles.alignCenter;
        if (align === 'left') return styles.alignLeft;
        return undefined;
    };

    // Helper to get width style
    const getWidthStyle = (width?: string | number) => {
        if (!width || String(width).trim() === '' || width === 'auto') {
            return {};
        }
        return {
            width,
        };
    };

    return (
        <div className={className}>
            {title && <h3 className={styles.title}>{title}</h3>}
            <div className={styles.tableContainer}>
                <div style={{ position: 'relative' }}>
                    {isLoading && data.length > 0 && (
                        <div className={styles.loadingOverlay}>
                            <StanLoader />
                        </div>
                    )}
                    <table>
                        {(!hideHeaderWhenEmpty || data.length > 0) && (
                            <thead>
                                {table.getHeaderGroups().map(headerGroup => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map(header => {
                                            const colDef = header.column.columnDef as StanColumnDef<T>;
                                            const align = colDef.align;
                                            const width =
                                                colDef.width && String(colDef.width).trim() !== ''
                                                    ? colDef.width
                                                    : 'auto';
                                            if (isMobile && colDef.hideOnMobile) return null;
                                            return (
                                                <th
                                                    key={header.id}
                                                    className={clsx(
                                                        getAlignClass(align),
                                                        'text-truncate',
                                                        styles.sortableHeader
                                                    )}
                                                    scope='col'
                                                    style={{
                                                        ...getWidthStyle(width),
                                                    }}
                                                >
                                                    {header.isPlaceholder ? null : (
                                                        <span>
                                                            {flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext()
                                                            )}
                                                        </span>
                                                    )}
                                                </th>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </thead>
                        )}
                        <tbody>
                            {isLoading && data.length === 0 ? (
                                <tr className={styles.notHoverable}>
                                    <td colSpan={visibleColumns.length} className={styles.loadingCell}>
                                        <StanLoader />
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr className={styles.notHoverable}>
                                    <td colSpan={visibleColumns.length}>
                                        {BareEmptyIcon}
                                        <div className={styles.emptyTextContainer}>
                                            <h2 className={styles.emptyHeader}>{emptyHeader}</h2>
                                            {emptySubheader && (
                                                <p
                                                    dangerouslySetInnerHTML={{ __html: emptySubheader }}
                                                    className={styles.emptySubheader}
                                                />
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                table.getRowModel().rows.map(row => (
                                    <tr
                                        key={row.id}
                                        onClick={onRowClick ? () => onRowClick(row.original as T) : undefined}
                                        tabIndex={onRowClick ? 0 : undefined}
                                        aria-label={onRowClick ? 'Row clickable' : undefined}
                                        style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                                    >
                                        {row
                                            .getVisibleCells()
                                            .filter(cell => {
                                                const columnDef = cell.column.columnDef as StanColumnDef<T>;
                                                return !(isMobile && columnDef.hideOnMobile);
                                            })
                                            .map(cell => {
                                                const columnDef = cell.column.columnDef as StanColumnDef<T>;
                                                const align = columnDef.align;
                                                let customClass = columnDef.cellClassName;
                                                if (typeof customClass === 'function') {
                                                    customClass = customClass(cell.getContext());
                                                }
                                                const width =
                                                    columnDef.width && String(columnDef.width).trim() !== ''
                                                        ? columnDef.width
                                                        : 'auto';
                                                return (
                                                    <td
                                                        key={cell.id}
                                                        className={clsx(
                                                            getAlignClass(align),
                                                            customClass,
                                                            'text-truncate'
                                                        )}
                                                        style={{
                                                            ...getWidthStyle(width),
                                                        }}
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                );
                                            })}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <div className={styles.tableFooter}>
                    {totalRowsLabel && <p>{totalRowsLabel}</p>}{' '}
                    {data.length > 0 && totalPages && totalPages > 1 && (
                        <div className={styles.paginationInfo}>
                            Page {page} of {totalPages}
                            <div className={styles.paginationButtons}>
                                <button
                                    onClick={() => onPageChange && page > 1 && onPageChange(page - 1)}
                                    disabled={page <= 1}
                                    aria-label='Previous page'
                                    className={clsx(styles.paginationButton, page <= 1 ? styles.disabled : '')}
                                >
                                    {BarePaginationLeftIcon}
                                </button>
                                <button
                                    onClick={() =>
                                        onPageChange && totalPages && page < totalPages && onPageChange(page + 1)
                                    }
                                    disabled={page >= totalPages}
                                    aria-label='Next page'
                                    className={clsx(styles.paginationButton, page >= totalPages ? styles.disabled : '')}
                                >
                                    {BarePaginationRightIcon}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
